import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import styled from "styled-components";
import { BsArrowRepeat, BsThreeDotsVertical } from "react-icons/bs";
import { MdFileDownload } from "react-icons/md";

const RefundsTab = ({ partnerId }) => {
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const {
    AuthReducer: { auth },
  } = useSelector((state) => state);

  useEffect(() => {
    fetchPartnerRefunds();
  }, [partnerId]);

  const fetchPartnerRefunds = async () => {
    try {
      const response = await axios.get(
        `/api/admin/v1/partner-refunds/${partnerId}`,
        {
          headers: { Authorization: `Bearer ${auth?.token}` },
        }
      );
      setRefunds(response.data.refunds || []);
    } catch (error) {
      console.error("Failed to fetch partner refunds");
    } finally {
      setLoading(false);
    }
  };

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");

  const handleExportRequest = async () => {
    try {
      const response = await axios.get(`/api/admin/v1/partner-refunds/${partnerId}/export`, {
        headers: { Authorization: `Bearer ${auth?.token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `partner-refunds-${partnerId}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      const csvData = filteredRefunds.map((refund, index) => ({
        "SL. No.": index + 1,
        "Request ID": refund.refund_id,
        "Booking ID": refund.booking_id,
        "Partner": refund.partner,
        "Customer": refund.customer,
        "Reason": refund.reason,
        "Requested Date": new Date(refund.refund_date).toLocaleDateString(),
        "Amount": `Rs.${parseFloat(refund.amount).toLocaleString()}`,
        "Status": refund.status
      }));
      const csv = [Object.keys(csvData[0]).join(','), ...csvData.map(row => Object.values(row).join(','))].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `partner-refunds-${partnerId}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    }
  };

  const clearFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setDateFilter("all");
  };

  const filteredRefunds = refunds.filter(refund => {
    const matchesSearch = !searchTerm || 
      refund.refund_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      refund.customer.toLowerCase().includes(searchTerm.toLowerCase()) ||
      refund.booking_id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || refund.status.toLowerCase() === statusFilter.toLowerCase();
    return matchesSearch && matchesStatus;
  });

  return (
    <Wrapper>
      <div className="card border-0 shadow-sm p-4">
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap">
          <div>
            <h4 className="text-dark mb-1 d-flex align-items-center">
              Refund & Cancelation
            </h4>
            <p className="text-muted mb-0">
              Manage and process refund and cancellation requests for this
              partner
            </p>
          </div>
          <div className="d-flex gap-3 mt-2 mt-md-0">
            <button
              className="btn btn-primary d-flex align-items-center"
              onClick={handleExportRequest}
            >
              <MdFileDownload className="me-2" size={20} />
              Export Request
            </button>
          </div>
        </div>

        <div className="d-flex align-items-center flex-wrap gap-2 mb-3 filter-box">
          <input
            type="text"
            className="form-control custom-select"
            placeholder="Search ID, Customer, Serv..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <select className="form-select custom-select" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)}>
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">Last Week</option>
            <option value="month">Last Month</option>
          </select>
          <select className="form-select custom-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All Statuses</option>
            <option value="approved">Approved</option>
            <option value="requested">Requested</option>
            <option value="rejected">Rejected</option>
            <option value="processing">Processing</option>
          </select>
          <button className="btn btn-outline-secondary d-flex align-items-center gap-1 ms-2" onClick={clearFilters}>
            <BsArrowRepeat size={16} />
            Clear
          </button>
        </div>

        <TableWrapper>
          <table className="table table-bordered align-middle">
            <thead className="table-light">
              <tr>
                <th>Request ID</th>
                <th>Booking ID</th>
                <th>Partner Type</th>
                <th>Partner</th>
                <th>Customer</th>
                <th>Reason</th>
                <th>Requested Date</th>
                <th>Amount</th>
                <th>Status</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRefunds.map((refund, index) => (
                <tr key={index}>
                  <td>
                    <strong>{refund.refund_id}</strong>
                  </td>
                  <td>
                    <strong>{refund.booking_id}</strong>
                  </td>
                  <td>
                    <span className="badge badge-business">Business</span>
                  </td>
                  <td>{refund.partner}</td>
                  <td>
                    {refund.customer}
                    <br />
                    <small className="text-muted">
                      {refund.customer_phone}
                    </small>
                  </td>
                  <td>{refund.reason}</td>
                  <td>{new Date(refund.refund_date).toLocaleDateString()}</td>
                  <td>₹{parseFloat(refund.amount).toLocaleString()}</td>
                  <td>
                    <span
                      className={`badge ${
                        refund.status.toLowerCase() === "approved"
                          ? "badge-approved"
                          : refund.status.toLowerCase() === "requested"
                          ? "badge-requested"
                          : refund.status.toLowerCase() === "rejected"
                          ? "badge-rejected"
                          : "badge-processing"
                      }`}
                    >
                      {refund.status}
                    </span>
                  </td>
                  <td className="text-center">
                    <BsThreeDotsVertical />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrapper>
      </div>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  .filter-box input,
  .filter-box select {
    width: 169px !important;
  }
  select.form-select.custom-select {
    width: 263px !important;
  }

  input.form-control.custom-select {
    width: 243px !important;
  }
`;

const TableWrapper = styled.div`
  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;
  }

  th,
  td {
    padding: 10px 12px;
    border: 1px solid #dee2e6;
    text-align: center;
    vertical-align: middle;
  }

  th {
    background-color: #f8f9fa;
    font-weight: 600;
  }

  .badge {
    font-size: 0.75rem;
    font-weight: 500;
    border-radius: 12px;
    padding: 4px 10px;
    display: inline-block;
  }

  .badge-business {
    background-color: rgba(0, 123, 255, 0.1);
    color: #0d6efd;
  }

  .badge-approved {
    background-color: rgba(25, 135, 84, 0.15);
    color: #198754;
  }

  .badge-requested {
    background-color: rgba(255, 193, 7, 0.15);
    color: #ffc107;
  }

  .badge-rejected {
    background-color: rgba(220, 53, 69, 0.15);
    color: #dc3545;
  }

  .badge-processing {
    background-color: rgba(13, 202, 240, 0.2);
    color: #0dcaf0;
  }

  .text-center {
    text-align: center;
  }
`;

export default RefundsTab;
